<div class="w-full max-w-full">
  
  <!-- Header -->
  <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
    <div>
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Kampanie</h1>
      <p class="text-gray-600 dark:text-gray-400 text-sm">Zarządzanie kampaniami newsletterowymi</p>
    </div>
    
    <button mat-flat-button color="primary" (click)="createCampaign()">
      <mat-icon [svgIcon]="'tabler_outline:plus'" class="mr-2"></mat-icon>
      Nowa kampania
    </button>
  </div>

  <!-- Loading -->
  <div *ngIf="isLoading" class="flex items-center justify-center py-12">
    <mat-spinner diameter="36"></mat-spinner>
  </div>

  <!-- Empty state -->
  <div *ngIf="!isLoading && campaigns.length === 0" class="bg-card rounded-xl shadow p-8 text-center">
    <div class="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
      <mat-icon [svgIcon]="'tabler_outline:mail'" class="!w-8 !h-8 text-gray-400"></mat-icon>
    </div>
    <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-1">Brak kampanii</h3>
    <p class="text-gray-500 dark:text-gray-400 text-sm mb-4">Utwórz pierwszą kampanię newsletterową</p>
    <button mat-flat-button color="primary" (click)="createCampaign()">
      <mat-icon [svgIcon]="'tabler_outline:plus'" class="mr-2"></mat-icon>
      Utwórz kampanię
    </button>
  </div>

  <!-- Campaigns list - compact -->
  <div *ngIf="!isLoading && campaigns.length > 0" class="bg-card rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
    
    <div *ngFor="let campaign of campaigns; let last = last" 
         class="flex items-stretch hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
         [class.border-b]="!last"
         [class.border-gray-100]="!last"
         [class.dark:border-gray-800]="!last">
      
      <!-- Status indicator -->
      <div class="w-1 flex-shrink-0"
           [ngClass]="{
             'bg-gray-300 dark:bg-gray-600': campaign.status === 'draft',
             'bg-blue-500': campaign.status === 'pending',
             'bg-green-500': campaign.status === 'sending',
             'bg-amber-500': campaign.status === 'stopped',
             'bg-purple-500': campaign.status === 'finished'
           }"></div>

      <!-- Main content -->
      <div class="flex-1 flex items-center gap-4 p-4 min-w-0">
        
        <!-- Name & Subject -->
        <div class="flex-1 min-w-0 cursor-pointer" (click)="editCampaign(campaign)">
          <div class="flex items-center gap-2">
            <h3 class="font-semibold text-gray-900 dark:text-white truncate hover:text-primary-600 transition-colors">
              {{ campaign.name }}
            </h3>
            <span class="px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0" [ngClass]="getStatusClass(campaign.status)">
              {{ getStatusLabel(campaign.status) }}
            </span>
          </div>
          <p class="text-sm text-gray-500 dark:text-gray-400 truncate">{{ campaign.subject }}</p>
        </div>

        <!-- Stats counters -->
        <div class="hidden sm:flex items-center gap-6 text-center flex-shrink-0">
          <div class="w-14">
            <p class="text-lg font-bold text-gray-900 dark:text-white">{{ campaign.stats.totalRecipients }}</p>
            <p class="text-xs text-gray-400">Odbiorcy</p>
          </div>
          <div class="w-14">
            <p class="text-lg font-bold text-blue-600 dark:text-blue-400">{{ campaign.stats.sent }}</p>
            <p class="text-xs text-gray-400">Wysłano</p>
          </div>
          <div class="w-14">
            <p class="text-lg font-bold text-green-600 dark:text-green-400">{{ campaign.stats.opened }}</p>
            <p class="text-xs text-gray-400">Otwarto</p>
          </div>
          <div class="w-10">
            <p class="text-lg font-bold text-red-500">{{ campaign.stats.errors }}</p>
            <p class="text-xs text-gray-400">Błędy</p>
          </div>
        </div>

        <!-- Vertical progress bars -->
        <div class="flex items-end gap-2 h-12 flex-shrink-0">
          <!-- Sent progress -->
          <div class="relative w-4 h-full bg-gray-200 dark:bg-gray-700 rounded-sm overflow-hidden" 
               [matTooltip]="'Wysłano: ' + campaign.stats.sent + ' / ' + campaign.stats.totalRecipients + ' (' + getSentProgress(campaign) + '%)'">
            <div class="absolute bottom-0 left-0 right-0 transition-all duration-300 rounded-sm"
                 [style.height.%]="getSentProgress(campaign)"
                 [style.background]="getProgressColor(getSentProgress(campaign))"></div>
          </div>
          <!-- Opened progress -->
          <div class="relative w-4 h-full bg-gray-200 dark:bg-gray-700 rounded-sm overflow-hidden"
               [matTooltip]="'Otwarto: ' + getOpenedProgress(campaign) + '%'">
            <div class="absolute bottom-0 left-0 right-0 transition-all duration-300 rounded-sm"
                 [style.height.%]="getOpenedProgress(campaign)"
                 [style.background]="getProgressColor(getOpenedProgress(campaign))"></div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center gap-1 flex-shrink-0">
          <button *ngIf="campaign.status === 'draft'" 
                  mat-icon-button 
                  color="primary"
                  (click)="startCampaign(campaign)" 
                  matTooltip="Uruchom">
            <mat-icon [svgIcon]="'tabler_outline:player-play'" class="!w-5 !h-5"></mat-icon>
          </button>
          <button *ngIf="campaign.status === 'pending' || campaign.status === 'sending'" 
                  mat-icon-button 
                  color="warn"
                  (click)="stopCampaign(campaign)" 
                  matTooltip="Wstrzymaj">
            <mat-icon [svgIcon]="'tabler_outline:player-pause'" class="!w-5 !h-5"></mat-icon>
          </button>
          <button *ngIf="campaign.status === 'stopped'" 
                  mat-icon-button 
                  color="primary"
                  (click)="resumeCampaign(campaign)" 
                  matTooltip="Wznów">
            <mat-icon [svgIcon]="'tabler_outline:player-play'" class="!w-5 !h-5"></mat-icon>
          </button>
          
          <button mat-icon-button [matMenuTriggerFor]="campaignMenu" matTooltip="Więcej opcji">
            <mat-icon [svgIcon]="'tabler_outline:dots-vertical'" class="!w-5 !h-5"></mat-icon>
          </button>
          
          <mat-menu #campaignMenu="matMenu">
            <button mat-menu-item (click)="editCampaign(campaign)" [disabled]="campaign.status === 'sending'">
              <mat-icon [svgIcon]="'tabler_outline:edit'"></mat-icon>
              <span>Edytuj</span>
            </button>
            <button mat-menu-item (click)="openRecipientsDialog(campaign)">
              <mat-icon [svgIcon]="'tabler_outline:users'"></mat-icon>
              <span>Odbiorcy</span>
            </button>
            <button mat-menu-item (click)="copyCampaignWithErrors(campaign)" [disabled]="campaign.stats.errors === 0">
              <mat-icon [svgIcon]="'tabler_outline:copy'"></mat-icon>
              <span>Kopiuj do odbiorców z błędami ({{ campaign.stats.errors }})</span>
            </button>
            <mat-divider></mat-divider>
            <button mat-menu-item (click)="deleteCampaign(campaign)" [disabled]="campaign.status === 'sending'">
              <mat-icon [svgIcon]="'tabler_outline:trash'" class="text-red-500"></mat-icon>
              <span class="text-red-500">Usuń</span>
            </button>
          </mat-menu>
        </div>
      </div>
    </div>
  </div>
</div>
